/**
 * Service › Téléphonie (service.telephonie.appels) – règles partagées API / front du POC du 23/10/2026 : journal des
 * appels (3CX ou simulation), transcription et résumé, rattachement aux objets AGORA.
 * Valeurs identiques aux contraintes de la migration db/migrations/service/20261023_1500_service_telephonie.sql.
 */

export const RESSOURCE_APPELS = 'service.telephonie.appels';
export const MODULE_TELEPHONIE = 'service.telephonie';

/**
 * Origine du journal : le serveur 3CX (modèle d'intégration CRM), le softphone AGORA (pilote Amazon Connect :
 * évènements de fin d'appel) ou le script de simulation du POC.
 */
export const SOURCES_APPEL = ['3cx', 'connect', 'simulation'] as const;
export const SENS_APPEL = ['entrant', 'sortant'] as const;
export const STATUTS_APPEL = ['repondu', 'manque', 'messagerie'] as const;

/**
 * Traitement de l'enregistrement : `aucun` (pas d'enregistrement déposé), `transcription` (Amazon Transcribe en
 * cours), `resume` (Claude en cours), `termine` (résumé disponible, ou transcription seule si le résumé IA est
 * inactif), `erreur`.
 */
export const TRAITEMENTS_APPEL = ['aucun', 'transcription', 'resume', 'termine', 'erreur'] as const;
/**
 * Statut du résumé enregistré. Plus de circuit de validation depuis le 03/11/2026 (décision du DSI : « le résumé des
 * appels est un bonus, pas une nécessité ») : tout résumé est écrit `valide` et reste modifiable, par l'IA
 * (valide_par = systeme:telephonie) ou par l'utilisateur. `brouillon` ne reste que sur des appels plus anciens, affichés
 * de la même façon.
 */
export const STATUTS_RESUME = ['brouillon', 'valide'] as const;

/**
 * Transcription d'un appel : donnée de travail, jamais conservée (décision du DSI, Issue #241, 02/11/2026 : « on ne
 * sauvegarde pas la transcription, uniquement le résumé »). Elle ne sert qu'à rédiger le résumé : effacée dès que le
 * résumé est enregistré (par l'IA ou par l'utilisateur), sinon ce nombre de jours après
 * l'appel (tâche planifiée de la nuit), comme l'enregistrement audio (paramètre ConservationEnregistrementsJours de la
 * pile data, 30 par défaut : à changer ensemble). Contrainte appel_resume_sans_transcription
 * (migration service/20261102_1500).
 */
export const CONSERVATION_TRANSCRIPTION_JOURS = 30;

/**
 * Objets auxquels un appel peut être rattaché (affecté) : clé = nœud du catalogue, déclarée dans le registre du socle
 * des objets affectables (api/src/liens/objets.ts). Un nouveau module s'ajoute ici, à la contrainte de
 * service.appel_lien (migration) et au registre.
 */
export const RESSOURCES_LIEES_APPEL = [
  'tiers', 'contacts', 'commerce.vente.affaires', 'commerce.prospection.pistes', 'it.demandes.incidents',
] as const;
export type RessourceLieeAppel = (typeof RESSOURCES_LIEES_APPEL)[number];

/**
 * Origine d'un rattachement : `numero` (numéro connu de l'annuaire), `contexte` (appel lancé depuis la fiche de
 * l'objet), `ia` (objet cité dans la conversation, proposé par le résumé), `manuel`.
 */
export const ORIGINES_LIEN = ['numero', 'contexte', 'ia', 'manuel'] as const;
export const STATUTS_LIEN = ['propose', 'confirme', 'rejete'] as const;

/** Vues de la liste : tous, à traiter (aucun objet confirmé), mes appels. */
export const VUES_APPELS = ['tous', 'a-traiter', 'miens'] as const;

/** Enregistrements acceptés (dépôt S3 direct) : type MIME → extensions. */
export const TYPES_ENREGISTREMENT = {
  'audio/mpeg': ['mp3'],
  'audio/wav': ['wav'],
} as const;
export type TypeEnregistrement = keyof typeof TYPES_ENREGISTREMENT;
export const TAILLE_MAX_ENREGISTREMENT = 50 * 1024 * 1024;

// ------------------------------------------------------------------ softphone (pilote Amazon Connect)
/**
 * Groupe Entra des agents du pilote softphone (docs/DROITS.md) : le softphone d'AGORA leur est réservé, en plus du
 * droit « lire » sur les appels. Remplaçable par config.json › softphone.groupe.
 */
export const GROUPE_SOFTPHONE_PILOTE = 'AGORA-FR01-SERVICE-SOFTPHONE';

/** Groupe Entra du softphone d'un environnement : la prod a le sien, dev et test partagent `…_TEST`. */
export function groupeSoftphoneEnvironnement(env: string): string {
  return env === 'prod' ? GROUPE_SOFTPHONE_PILOTE : `${GROUPE_SOFTPHONE_PILOTE}_TEST`;
}

// ------------------------------------------------------------------ standard 3CX
/** Type d'appel du journal 3CX (variable CallType du scénario ReportCall). */
export const TYPES_APPEL_3CX = ['Inbound', 'Outbound', 'Missed', 'Notanswered'] as const;
export type TypeAppel3cx = (typeof TYPES_APPEL_3CX)[number];

/** CallType 3CX → sens et statut AGORA (Notanswered : appel sortant sans réponse). */
export function appelDepuis3cx(type: TypeAppel3cx): { sens: (typeof SENS_APPEL)[number]; statut: (typeof STATUTS_APPEL)[number] } {
  switch (type) {
    case 'Inbound': return { sens: 'entrant', statut: 'repondu' };
    case 'Outbound': return { sens: 'sortant', statut: 'repondu' };
    case 'Missed': return { sens: 'entrant', statut: 'manque' };
    case 'Notanswered': return { sens: 'sortant', statut: 'manque' };
  }
}

/** Durée « hh:mm:ss » (variable Duration de 3CX) → secondes. */
export function dureeEnSecondes(hms: string): number {
  const [h, m, s] = hms.split(':').map(Number);
  return Math.round(h * 3600 + m * 60 + s);
}

// ------------------------------------------------------------------ numéros
/** Numéro au format international E.164 (+33475000000). */
export const NUMERO_E164 = /^\+[1-9]\d{6,14}$/;

/**
 * Indicatif des pays des filiales et préfixe national à retirer (« 0 » en France). En Italie, le 0 initial fait
 * partie du numéro ; en Espagne et en Tchéquie, il n'y a pas de préfixe ; aux États-Unis et au Canada, le « 1 »
 * national est facultatif. POC : à remplacer par libphonenumber pour couvrir tous les pays.
 */
const INDICATIFS: Record<string, { indicatif: string; prefixe: string }> = {
  FR: { indicatif: '33', prefixe: '0' },
  IT: { indicatif: '39', prefixe: '' },
  ES: { indicatif: '34', prefixe: '' },
  DE: { indicatif: '49', prefixe: '0' },
  US: { indicatif: '1', prefixe: '' },
  CA: { indicatif: '1', prefixe: '' },
  BR: { indicatif: '55', prefixe: '0' },
  CZ: { indicatif: '420', prefixe: '' },
};

/**
 * Numéro saisi ou transmis par le standard → forme E.164, en interprétant un numéro national selon le pays de la
 * filiale. `null` si le numéro est vide ou invalide (numéro masqué, poste interne…).
 */
export function numeroInternational(brut: string | null | undefined, pays: string): string | null {
  // « +33 (0)4 … » : le zéro entre parenthèses ne se compose pas depuis l'étranger
  let n = (brut ?? '').trim().replace(/\(0\)/g, '').replace(/[\s.\-()/]/g, '');
  if (!n) return null;
  if (n.startsWith('00')) n = `+${n.slice(2)}`;
  if (!n.startsWith('+')) {
    const p = INDICATIFS[pays];
    if (!p || !/^\d+$/.test(n)) return null;
    if (p.indicatif === '1' && /^1\d{10}$/.test(n)) n = n.slice(1);
    else if (p.prefixe && n.startsWith(p.prefixe)) n = n.slice(p.prefixe.length);
    n = `+${p.indicatif}${n}`;
  }
  return NUMERO_E164.test(n) ? n : null;
}
